import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { MessageService } from '../../../core/services/messages/messages.service';
import { IMessageEvents } from '../../../constants/events.constants';
import { apiBaseUrls } from '../../../constants/environments.constants';
import { languageDetails, languages } from '../../../constants/language.constants';
import { environment } from '../../../../environments/environment';
import { NavigationEnd, Router } from '@angular/router';
import { AuthService } from '../../../core/services/auth/auth.service';
import { ResizeService } from '../../../core/services/resize/resize.service';
import { ViewportService } from '@shared/common/services/viewport/viewport.service';
import { TranslateService } from '@ngx-translate/core';
import { DOCUMENT } from '@angular/common';
import { ScrollToService } from '../../../core/services/scroll-to/scroll-to.service';
import { DateService } from '../../../core/services/date-service/date.service';
import { EnvironmentHelperService } from '../../../shared/services/environment-helper-service/environment-helper.service';

const hasOverlayClass = 'has-overlay';

const showMaintenance = DateService.showPlannedMaintenance();

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['../../../../assets/scss/swiper-navigation-buttons.scss', './header.component.scss'],
  standalone: false
})
export class HeaderComponent implements OnInit, OnDestroy {
  private readonly destruction$: Subject<void> = new Subject();
  menuIsOpen = false;
  langSwitcherIsOpen = false;
  offset: number;
  headerHeight: number;
  isSticky = false;
  isVisible = true; // used to make the header invisible when an overlay is open (e.g city picker, datepicker ...etc)
  @Input() shouldStick = true;
  @Input() isOffset: boolean;
  @Output() menuOpen: EventEmitter<boolean> = new EventEmitter(false);
  currentEnvironment = '';
  public homePath = showMaintenance ? '/orange-beta' : '/';
  isLoggedIn: boolean;
  isPostBookingPage: boolean;
  isCheckoutPage: boolean;
  shouldResetHeader = false;
  @ViewChild('headerRef') headerElementRef: ElementRef;
  @ViewChild('headerContentRef') headerContentElementRef: ElementRef;

  get isDevEnvironment(): boolean {
    return ['local', 'develop'].includes(environment.environment) ?? false;
  }

  // Checking if it is prod or deux as we are using Wordpress there.
  // For wordpress environments we are including language param after the '/' for logo href
  // in order to redirect to translated homepage in current language --- eg. '/de'
  get isWordpressEnv(): boolean {
    return this.environmentHelperService.isWordpressEnv();
  }

  get currentLang(): string {
    return this.translateService.currentLang;
  }

  get isOffsetPosition() {
    return this.isOffset && !this.menuIsOpen;
  }

  /*
   * Why do we need shouldResetHeader:
   * The HP uses a separate shell component. That means the header dom element in the HP is different than the header dom element in the listing page.
   * After navigation, we need to reset this.header (the scroll event is triggered after NavigationEnd)
   * */
  constructor(
    private readonly renderer: Renderer2,
    private readonly messageService: MessageService,
    private readonly authService: AuthService,
    private readonly router: Router,
    public translateService: TranslateService,
    public viewportService: ViewportService,
    private readonly resizeService: ResizeService,
    private readonly scrollToService: ScrollToService,
    private readonly environmentHelperService: EnvironmentHelperService,
    @Inject(DOCUMENT) private readonly document: Document
  ) {
    // subscribe to home components messages
    this.messageService
      .getOverlayMessage()
      .pipe(takeUntil(this.destruction$))
      .subscribe((message) => {
        switch (message.text) {
          // set the header z-index by adding a css class so it doesn't overlap with another overlay
          case IMessageEvents.SHOW_HEADER:
            this.isVisible = true;
            break;
          case IMessageEvents.HIDE_HEADER:
            this.isVisible = false;
            break;
          case IMessageEvents.SHOW_OVERLAY:
            this.disableScroll();
            break;
          case IMessageEvents.HIDE_OVERLAY:
            this.enableScroll();
            break;
          default:
            break;
        }
      }),
      this.router.events.subscribe((event) => {
        const checkoutPagePattern = /^\/listings\/checkout/i;
        const postBookingPattern = /^\/guest-hub\/booking|\/online-check-in/i;

        if (event instanceof NavigationEnd) {
          this.shouldResetHeader = true;
          this.checkQueryLang(event);
          this.isCheckoutPage = checkoutPagePattern.test(event.url);
          this.isPostBookingPage = postBookingPattern.test(event.url);
        }
      }),
      this.authService.isLoggedInSubject.subscribe((isLoggedIn) => {
        this.isLoggedIn = isLoggedIn;
      });

    const { apiBaseUrl, environment: baseEnv } = environment;

    switch (`${apiBaseUrl}::${baseEnv}`) {
      case `${apiBaseUrls.localhost}::local`:
        this.currentEnvironment = 'Localhost';
        break;
      case `${apiBaseUrls.develop}::develop`:
        this.currentEnvironment = 'Develop';
        break;
      case `${apiBaseUrls.staging}::staging`:
        this.currentEnvironment = 'Staging';
        break;
      case `${apiBaseUrls.staging}::deux`:
        this.currentEnvironment = 'Deux';
        break;
      default:
        break;
    }
  }

  @HostListener('window:scroll', [])
  onScroll() {
    this.setStickyHeader();
  }
  setStickyHeader() {
    if (this.shouldResetHeader) {
      this.shouldResetHeader = false;
    }

    if (this.isOffset) {
      this.isSticky = true;
    } else {
      this.isSticky = this.shouldStick && window.pageYOffset > this.headerElementRef.nativeElement.offsetTop;
    }

    this.offset = window.pageYOffset;
  }
  ngOnInit() {
    // Remove preload class from body: used to disable transitions on page load
    setTimeout(() => {
      this.renderer.removeClass(this.document.body, 'preload');
    }, 250);

    let prevWidth = window.innerWidth;

    this.resizeService
      .resize()
      .pipe(takeUntil(this.destruction$))
      .subscribe(() => {
        // Disable animations
        this.renderer.addClass(this.document.body, 'preload');
        this.setStickyHeader();
        this.renderer.removeClass(this.document.body, 'preload');

        if (window.innerWidth !== prevWidth) {
          this.langSwitcherIsOpen = false;
          prevWidth = window.innerWidth;
          this.toggleMenu('close');
        }
      });
  }

  get isMobile() {
    return this.viewportService.isMobileDevice();
  }

  scrollToPageTop() {
    this.scrollToService.scrollToPageTop();
  }

  openLangSwitch() {
    this.langSwitcherIsOpen = true;

    if (this.isMobile) {
      this.disableScroll();
      this.checkHeaderPosition();
    }
  }
  closeLangSwitch() {
    this.langSwitcherIsOpen = false;

    if (this.isMobile && !this.menuIsOpen) {
      this.enableScroll();
    }
  }
  checkQueryLang(event) {
    const currentLang = this.translateService.currentLang as languages;

    for (const lang of Object.values(languageDetails)) {
      if (event.url.indexOf(lang.query) >= 0 && lang.code != currentLang) {
        this.messageService.sendLanguageChange(lang.code);
        break;
      }
    }
  }
  toggleMenu(action?: string) {
    if (action) {
      this.menuIsOpen = action === 'close';
    }

    this.menuIsOpen = !this.menuIsOpen;
    this.menuOpen.emit(this.menuIsOpen);

    if (this.menuIsOpen) {
      this.disableScroll();
      this.checkHeaderPosition();
    } else {
      this.enableScroll();
    }
  }

  enableScroll() {
    this.renderer.removeClass(this.document.body, hasOverlayClass);
  }

  disableScroll() {
    this.renderer.addClass(this.document.body, hasOverlayClass);
  }

  checkHeaderPosition() {
    if (this.isMobile && this.headerContentElementRef.nativeElement.offsetTop !== 0) {
      this.scrollToService.scrollToTop(0, '.header', this.document.querySelector('.header')?.clientHeight);
    }
  }

  handleKeyDown(event: KeyboardEvent) {
    if (this.menuIsOpen && event.code === 'Escape') {
      event.preventDefault();
      this.menuIsOpen = false;
    }
  }

  ngOnDestroy() {
    this.destruction$.next();
    this.destruction$.complete();
  }
}
